import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function SignupForm({ onSent }) {
  const { signup } = useAuth();
  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await signup(form);
      if (data?.verification_required) {
        if (data.email_sent === false) {
          toast.warning(
            "Account saved, but we couldn't email the code right now (our email provider's daily quota may be full). Please retry in a few minutes or contact support.",
            { duration: 8000 },
          );
        } else if (data.resumed) {
          toast.success("Welcome back — we've sent you a fresh verification code.");
        } else {
          toast.success("Account created! Check your inbox for the 6-digit code.");
        }
        onSent(form.email);
      } else {
        // Backwards compatibility with older backend (shouldn't happen).
        toast.success("Welcome to MenuMojo!");
        window.location.href = "/dashboard";
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
      <div>
        <Label htmlFor="name">Your name</Label>
        <Input id="name" required value={form.name} onChange={onChange("name")} className="mt-1.5" data-testid="signup-name-input" />
      </div>
      <div>
        <Label htmlFor="restaurant_name">Restaurant name</Label>
        <Input id="restaurant_name" required value={form.restaurant_name} onChange={onChange("restaurant_name")} className="mt-1.5" data-testid="signup-restaurant-input" />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" required value={form.email} onChange={onChange("email")} className="mt-1.5" data-testid="signup-email-input" />
      </div>
      <div>
        <Label htmlFor="password">Password</Label>
        <PasswordInput
          id="password"
          required
          minLength={6}
          value={form.password}
          onChange={onChange("password")}
          autoComplete="new-password"
          className="mt-1.5"
          testIdPrefix="signup-password"
          data-testid="signup-password-input"
        />
      </div>
      {error && <div className="text-sm text-destructive" data-testid="signup-error">{error}</div>}
      <Button type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold" data-testid="signup-submit-button">
        {loading ? "Creating your account…" : "Start 1-day free trial"}
      </Button>
    </form>
  );
}

function VerifyStep({ email, onBack }) {
  const { verifySignupOtp, resendSignupOtp } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const timerRef = useRef(null);

  const startResendTimer = (seconds = 30) => {
    setResendIn(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // Kicks off the cooldown countdown the moment we land on the verify step.
    // The setState inside is intentional — disabling the new lint rule that
    // generically blocks setState-in-effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    startResendTimer(30);
    return () => clearInterval(timerRef.current);
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verifySignupOtp(email, code.trim());
      toast.success("Email verified — welcome to MenuMojo!");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError(""); setLoading(true);
    try {
      const data = await resendSignupOtp(email);
      if (data && data.email_sent === false) {
        toast.warning(
          "Couldn't send a new code right now. Our email provider's daily quota may be full — please try again in a few minutes or contact support.",
          { duration: 8000 },
        );
      } else {
        toast.success("Verification code sent again.");
      }
      startResendTimer(30);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={verify} className="mt-8 space-y-4" data-testid="signup-verify-form">
      <div>
        <Label htmlFor="signup-otp">6-digit verification code</Label>
        <Input
          id="signup-otp"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          required
          autoFocus
          autoComplete="one-time-code"
          placeholder="••••••"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="mt-1.5 tracking-[0.5em] text-center text-lg font-bold"
          data-testid="signup-otp-input"
        />
        <p className="text-xs text-foreground/60 mt-2">
          Sent to <span className="font-semibold">{email}</span>. Expires in 10 minutes.
        </p>
      </div>
      {error && <div className="text-sm text-destructive" data-testid="signup-verify-error">{error}</div>}
      <Button
        type="submit"
        disabled={loading || code.length < 4}
        className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold"
        data-testid="signup-verify-button"
      >
        {loading ? "Verifying…" : "Verify & enter dashboard"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onBack}
          className="text-foreground/60 hover:underline"
          data-testid="signup-back-button"
        >
          Use a different email
        </button>
        <button
          type="button"
          disabled={resendIn > 0 || loading}
          onClick={resend}
          className="brand-orange font-semibold hover:underline disabled:text-foreground/40 disabled:no-underline"
          data-testid="signup-resend-button"
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}

export default function Signup() {
  const [step, setStep] = useState("form"); // form | verify
  const [email, setEmail] = useState("");

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:flex bg-brand-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 70% 20%, #FF6500 0%, transparent 40%), radial-gradient(circle at 20% 80%, #FF6500 0%, transparent 35%)"
        }} />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Logo size={48} variant="dark" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">Try it free for 1 day</div>
            <h1 className="font-heading font-bold text-4xl xl:text-5xl leading-tight">
              Your digital menu<br />is 2 minutes away.
            </h1>
            <ul className="mt-6 space-y-2 text-white/70 text-sm">
              <li>• 1-day trial · 1 outlet included</li>
              <li>• Upgrade any time — plans from ₹1,000/year</li>
              <li>• Custom branded QR codes</li>
              <li>• Real-time updates & bulk Excel import</li>
            </ul>
          </div>
          <div className="text-xs text-white/40">© 2026 MenuMojo</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>

          {step === "form" ? (
            <>
              <h2 className="font-heading font-bold text-3xl">Create your account</h2>
              <p className="text-foreground/60 mt-2 text-sm">Start building your restaurant menu.</p>
              <SignupForm onSent={(e) => { setEmail(e); setStep("verify"); }} />
            </>
          ) : (
            <>
              <h2 className="font-heading font-bold text-3xl">Verify your email</h2>
              <p className="text-foreground/60 mt-2 text-sm">
                One more step &mdash; enter the 6-digit code we just emailed you.
              </p>
              <VerifyStep email={email} onBack={() => setStep("form")} />
            </>
          )}

          <div className="mt-6 text-sm text-foreground/60">
            Already have an account?{" "}
            <Link to="/login" className="brand-orange font-semibold hover:underline" data-testid="login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
